import { splitCodeLines } from './pythonSourceScanUtils';

/** Matches `varName.start(` or `RobotBase().start(` at the start of a line. */
export const ROBOT_START_CALL_LINE_RE = /^\s*([A-Za-z_][\w]*)(?:\s*\(\s*\))?\s*\.\s*start\s*\(/;
const DIRECT_ROBOTBASE_START_CALL_LINE_RE = /^\s*RobotBase\s*\(\s*\)\s*\.\s*start\s*\(/;
const ROBOTBASE_ASSIGNMENT_LINE_RE = /^\s*([A-Za-z_][\w]*)\s*=\s*RobotBase\s*\(/;
const ASSIGNMENT_LINE_RE = /^\s*([A-Za-z_][\w]*)\s*=/;

/** Returns the 1-based line number of the first `.start()` call, or null if not found. */
export function findRobotStartLineInCode(code: string): number | null {
  const lines = splitCodeLines(code);
  for (let i = 0; i < lines.length; i++) {
    if (ROBOT_START_CALL_LINE_RE.test(lines[i] ?? '')) return i + 1;
  }
  return null;
}

export function getRobotBaseVariableNamesInCode(code: string): Set<string> {
  const variableNames = new Set<string>();
  for (const line of splitCodeLines(code)) {
    const match = line.match(ROBOTBASE_ASSIGNMENT_LINE_RE);
    if (match?.[1]) variableNames.add(match[1]);
  }
  return variableNames;
}

export function isManagedRobotStartCallLine(line: string, robotBaseVariableNames: Set<string>): boolean {
  if (DIRECT_ROBOTBASE_START_CALL_LINE_RE.test(line)) return true;

  const match = line.match(ROBOT_START_CALL_LINE_RE);
  return match?.[1] ? robotBaseVariableNames.has(match[1]) : false;
}

export function getManagedRobotStartLineNumbersInCode(code: string): Set<number> {
  const managedStartLines = new Set<number>();
  const robotBaseVariableNames = new Set<string>();
  const lines = splitCodeLines(code);

  for (let index = 0; index < lines.length; index++) {
    const line = lines[index] ?? '';
    const lineNumber = index + 1;

    if (DIRECT_ROBOTBASE_START_CALL_LINE_RE.test(line)) {
      managedStartLines.add(lineNumber);
    } else {
      const startMatch = line.match(ROBOT_START_CALL_LINE_RE);
      if (startMatch?.[1] && robotBaseVariableNames.has(startMatch[1])) {
        managedStartLines.add(lineNumber);
      }
    }

    const robotBaseAssignmentMatch = line.match(ROBOTBASE_ASSIGNMENT_LINE_RE);
    if (robotBaseAssignmentMatch?.[1]) {
      robotBaseVariableNames.add(robotBaseAssignmentMatch[1]);
      continue;
    }

    const assignmentMatch = line.match(ASSIGNMENT_LINE_RE);
    if (assignmentMatch?.[1]) robotBaseVariableNames.delete(assignmentMatch[1]);
  }

  return managedStartLines;
}

export function hasManagedRobotStartCallInCode(code: string): boolean {
  return getManagedRobotStartLineNumbersInCode(code).size > 0;
}

/** Returns 1-based insert line before `.start()`, keeping a comment line above start attached to it. */
export function getInsertLineBeforeRobotStartInCode(code: string): number | null {
  const startLine = findRobotStartLineInCode(code);
  if (startLine === null) return null;
  if (startLine > 1) {
    const lineAbove = splitCodeLines(code)[startLine - 2]?.trim() ?? '';
    if (lineAbove.startsWith('#')) return startLine - 1;
  }
  return startLine;
}
